import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

/**
 * AppDialogs — globalny provider stylowanych modali (confirm / prompt / alert).
 * Zastępuje natywne window.confirm/prompt/alert brzydkie okna przeglądarki.
 *
 * Użycie: mount <AppDialogs /> raz w App.js. Monkey-patchuje window.* na wersje
 * zwracające Promise. Wszystkie call-sites w kodzie zostały zmienione na:
 *   if (!(await window.confirm('...')))
 *   const value = await window.prompt('...', 'default')
 *   await window.alert('...')
 */

let dialogController = null;

const AppDialogs = () => {
  const [state, setState] = useState(null); // { kind, message, defaultValue, resolve }

  const close = useCallback((val) => {
    if (state?.resolve) state.resolve(val);
    setState(null);
  }, [state]);

  useEffect(() => {
    dialogController = {
      confirm: (msg) => new Promise((resolve) => setState({ kind: 'confirm', message: String(msg || ''), resolve })),
      prompt: (msg, def) => new Promise((resolve) => setState({ kind: 'prompt', message: String(msg || ''), defaultValue: String(def || ''), resolve })),
      alert: (msg) => new Promise((resolve) => setState({ kind: 'alert', message: String(msg || ''), resolve })),
    };
    // Monkey-patch — musi być zaraz po zamocowaniu.
    const orig = { confirm: window.confirm, prompt: window.prompt, alert: window.alert };
    window.confirm = (msg) => dialogController.confirm(msg);
    window.prompt = (msg, def) => dialogController.prompt(msg, def);
    window.alert = (msg) => dialogController.alert(msg);
    return () => {
      window.confirm = orig.confirm;
      window.prompt = orig.prompt;
      window.alert = orig.alert;
      dialogController = null;
    };
  }, []);

  const [promptValue, setPromptValue] = useState('');
  useEffect(() => { setPromptValue(state?.defaultValue || ''); }, [state]);

  if (!state) return null;
  const { kind, message } = state;

  const overlay = (
    <div
      onClick={() => kind === 'alert' ? close() : close(kind === 'prompt' ? null : false)}
      data-testid="app-dialog-overlay"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100000, padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{
          background: 'var(--panel-bg, #1a1a1e)',
          border: '1px solid var(--border, #2a2a2e)',
          borderRadius: 14, padding: 24, maxWidth: 440, width: '100%',
          color: 'var(--text, #fafafa)', fontFamily: 'inherit',
          boxShadow: '0 24px 48px rgba(0,0,0,0.5)',
        }}
      >
        <div style={{
          fontSize: 12, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase',
          color: 'var(--gold, #d4af37)', marginBottom: 10,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          {kind === 'confirm' && '⚠️ Potwierdź akcję'}
          {kind === 'prompt' && '✏️ Wprowadź wartość'}
          {kind === 'alert' && 'ℹ️ Informacja'}
        </div>
        <div style={{ fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap', marginBottom: 16 }}>
          {message}
        </div>
        {kind === 'prompt' && (
          <input
            autoFocus
            value={promptValue}
            onChange={(e) => setPromptValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') close(promptValue); if (e.key === 'Escape') close(null); }}
            data-testid="app-dialog-input"
            style={{
              width: '100%', padding: '10px 12px', marginBottom: 14,
              borderRadius: 8, border: '1px solid var(--border, #2a2a2e)',
              background: 'var(--bg-card, #0f0f13)', color: 'var(--text, #fafafa)',
              fontSize: 14,
            }}
          />
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          {kind !== 'alert' && (
            <button
              onClick={() => close(kind === 'prompt' ? null : false)}
              data-testid="app-dialog-cancel"
              style={{
                padding: '9px 18px', borderRadius: 8, border: '1px solid var(--border, #2a2a2e)',
                background: 'transparent', color: 'var(--text-muted, #999)', cursor: 'pointer',
                fontSize: 13, fontWeight: 700,
              }}
            >
              Anuluj
            </button>
          )}
          <button
            onClick={() => close(kind === 'confirm' ? true : kind === 'prompt' ? promptValue : undefined)}
            autoFocus={kind !== 'prompt'}
            data-testid="app-dialog-ok"
            style={{
              padding: '9px 18px', borderRadius: 8, border: 'none',
              background: 'linear-gradient(135deg, #d4af37, #b8941f)', color: '#000',
              cursor: 'pointer', fontSize: 13, fontWeight: 800,
            }}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
};

export default AppDialogs;
